import apiClient from "@/api/client";

import type {
  CashflowData,
  EmployeeStatusRow,
  ComparisonsData,
  FinRun,
  HeadToHeadData,
  OverheadConfigDto,
  OverviewData,
  OverviewMeta,
  PersonDetail,
  SuggestionsFeed,
  Variant,
  ZeShareRow,
} from "./types";

const BASE = "/fin-aron";

async function unwrap<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  const r = await p;
  return r.data.data;
}

async function unwrapWithMeta<T, M>(
  p: Promise<{ data: { data: T; meta: M } }>,
): Promise<{ data: T; meta: M }> {
  const r = await p;
  return { data: r.data.data, meta: r.data.meta };
}

export const getOverview = (variant: Variant) =>
  unwrapWithMeta<OverviewData, OverviewMeta>(
    apiClient.get(`${BASE}/overview/`, { params: { variant } }),
  );

export const getPersonDetail = (userId: string, variant: Variant, fresh = false) =>
  unwrap<PersonDetail>(
    apiClient.get(`${BASE}/designers/${userId}/`, {
      params: { variant, ...(fresh ? { fresh: "1" } : {}) },
    }),
  );

export const getOverheadConfig = (year?: number, month?: number) =>
  unwrap<OverheadConfigDto>(
    apiClient.get(`${BASE}/overhead-config/`, {
      params: { ...(year ? { year } : {}), ...(month ? { month } : {}) },
    }),
  );

export const putOverheadConfig = (body: {
  year: number;
  month: number;
  narzut_pct: string | null;
  window_months?: number;
}) => unwrap<OverheadConfigDto>(apiClient.put(`${BASE}/overhead-config/`, body));

export const getCashflow = (variant: Variant, designerId?: string) =>
  unwrap<CashflowData>(
    apiClient.get(`${BASE}/cashflow/`, {
      params: { variant, ...(designerId ? { designer: designerId } : {}) },
    }),
  );

export const getComparisons = (from?: string, to?: string) =>
  unwrap<ComparisonsData>(
    apiClient.get(`${BASE}/comparisons/`, {
      params: { ...(from ? { from } : {}), ...(to ? { to } : {}) },
    }),
  );

export const getSuggestions = (scope: "portfolio" | "designer", designerId?: string) =>
  unwrap<SuggestionsFeed>(
    apiClient.get(`${BASE}/suggestions/`, {
      params: { scope, ...(designerId ? { designer: designerId } : {}) },
    }),
  );

/** Last 30 runs for the subject — cost, tokens and why a run produced nothing. */
export const getRuns = (scope: "portfolio" | "designer", designerId?: string) =>
  unwrap<FinRun[]>(
    apiClient.get(`${BASE}/runs/`, {
      params: { scope, ...(designerId ? { designer: designerId } : {}) },
    }),
  );

export const triggerRun = (scope: "portfolio" | "designer", designerId?: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/runs/trigger/`, {
      scope,
      ...(designerId ? { designer: designerId } : {}),
    }),
  );

export const suggestionFeedback = (id: string, status: "done" | "dismissed") =>
  unwrap(apiClient.patch(`${BASE}/suggestions/${id}/feedback/`, { status }));

export const suggestionComment = (id: string, body: string) =>
  unwrap(apiClient.post(`${BASE}/suggestions/${id}/comment/`, { body }));

export const suggestionPromote = (id: string) =>
  unwrap<{ knowledge_entry_id: string; status: string }>(
    apiClient.post(`${BASE}/suggestions/${id}/promote/`, {}),
  );

export const getHeadToHead = (a: string, b: string, variant: Variant) =>
  unwrap<HeadToHeadData>(
    apiClient.get(`${BASE}/head-to-head/`, { params: { a, b, variant } }),
  );

export const getZeShares = (year: number, month: number) =>
  unwrap<{ designers: ZeShareRow[]; employees: EmployeeStatusRow[] }>(
    apiClient.get(`${BASE}/ze-shares/`, { params: { year, month } }),
  );

export const putProjectStatus = (body: {
  employee: string;
  year: number;
  month: number;
  is_non_project: boolean | null;
}) => unwrap(apiClient.put(`${BASE}/project-status/`, body));

export const putZeShare = (body: {
  designer: string;
  year: number;
  month: number;
  share_pct: string | null;
}) => unwrap(apiClient.put(`${BASE}/ze-shares/`, body));

export const getDirectives = () =>
  unwrap<{ text: string; author: string | null; updated_at: string | null; is_editor: boolean }>(
    apiClient.get(`${BASE}/directives/`),
  );

export const putDirectives = (text: string) =>
  unwrap<{ text: string }>(apiClient.put(`${BASE}/directives/`, { text }));
